import React, { useState, useEffect } from 'react';
import { Paper, TableBody, TableCell, TableHead, TableRow, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip, IconButton, Switch, Box, Alert } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import axios from '../../api/client';
import { formatAmount } from '../../utils/money';
import { sideLabel, balanceMagnitude, openingBalanceHint } from '../../utils/ledger';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const ACCT_TYPES = ['asset','liability','equity','income','expense'];

const today = () => new Date().toISOString().slice(0, 10);
// A date input takes yyyy-MM-dd; the API sends a full ISO timestamp.
const asDateInput = (value) => (value ? String(value).slice(0, 10) : '');
const asDisplayDate = (value) => (value ? new Date(value).toLocaleDateString() : null);

// A balance with the side it sits on. Both columns held a signed number whose meaning
// depended on the account's type and contra flag — 5,000 is a debit on Cash at Bank and a
// credit on a landlord's payable, and nothing said which. Dr/Cr is what a statement says,
// so it is what this says. Tabular figures so the column lines up.
const Balance = ({ amount, account }) => {
  const side = sideLabel(amount, account);
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.75, fontVariantNumeric: 'tabular-nums' }}>
      <Box component="span">{formatAmount(balanceMagnitude(amount))}</Box>
      <Box
        component="span"
        sx={{ minWidth: 18, color: side === 'Dr' ? 'info.main' : 'warning.main', fontWeight: 600 }}
      >
        {side}
      </Box>
    </Box>
  );
};

// Mirrors backend/utils/accountCategories.js — what kind of money an asset account holds.
// Mirrors backend/utils/accountCategories.js.
const ACCOUNT_CATEGORIES = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', suspense: 'Suspense', other: 'Other' };

/**
 * @param {function} [onDrillDown]  called with an account id to open that account's ledger.
 *   Optional — this screen is also rendered where there is nowhere to drill to.
 */
const ChartOfAccounts = ({ onDrillDown }) => {
  const [accounts, setAccounts] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server must search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleAccounts, resultCount, totalCount
  } = useTableSearch(accounts, {
    searchFields: ['code', 'name', 'type'],
    filters: {
      type: (row, v) => row.type === v,
      // The group an asset account belongs to. 'Suspense' is the one people look for: the
      // accounts money is parked in until it is moved on, which are otherwise scattered
      // among the other assets by code.
      category: (row, v) => (row.accountCategory || '') === v
    }
  });
  const [openDlg, setOpenDlg] = useState(false);
  const [editing, setEditing] = useState(null);

  // Whether the account being edited is one the posting code reaches by literal code —
  // 1100, 2300, 4500 and the rest of the built-in chart. Its code, type and active state
  // are fixed at the server; the dialog says so rather than letting someone change a field
  // and meet the refusal on save.
  const [structural, setStructural] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', type: '', description: '', openingBalance: '', openingBalanceDate: today(), isContraAccount: false, isActive: true, accountCategory: '' });
  // Whether the code in the box was put there by the system. Typing over it clears this, so
  // the helper text stops claiming a number the user chose was suggested.
  const [codeSuggested, setCodeSuggested] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);

  // A chart of accounts is numbered by type — assets in the 1000s, liabilities in the 2000s
  // — so the suggestion follows the type, and follows it again if the user changes their
  // mind. Only ever a suggestion: an agency numbering its accounts its own way types over
  // it, and the field stays editable.
  //
  // It will not overwrite a code already typed, and never touches one being edited: a
  // built-in account's code is fixed, and re-coding any account breaks what points at it.
  const suggestCode = async (type) => {
    if (editing || !type) return;
    if (form.code && !codeSuggested) return;
    setCodeLoading(true);
    try {
      const res = await axios.get(`/accounting/chart-of-accounts/next-code?type=${type}`);
      setForm(f => ({ ...f, code: res.data.code }));
      setCodeSuggested(true);
    } catch {
      // A crowded band or an unreachable server is not a reason to block the dialog — the
      // field is still there to type into, and the server refuses a duplicate anyway.
      setCodeSuggested(false);
    } finally {
      setCodeLoading(false);
    }
  };

  const fetchAccounts = async () => {
    const { data } = await axios.get('/accounting/chart-of-accounts');
    setAccounts(data);
  };

  useEffect(() => { fetchAccounts(); }, []);

  const handleOpen = (acc = null) => {
    if (acc) {
      setEditing(acc._id);
      setStructural(acc.isStructural ? (acc.structuralUse || true) : null);
      setForm({
        code: acc.code, name: acc.name, type: acc.type, description: acc.description||'',
        openingBalance: acc.initialBalance||0,
        // The date it was already opened on, so a correction lands in the same period as the
        // figure it corrects rather than today.
        openingBalanceDate: asDateInput(acc.openingBalanceDate) || today(),
        isContraAccount: !!acc.isContraAccount, isActive: !!acc.isActive, accountCategory: acc.accountCategory||''
      });
    } else {
      setEditing(null);
      setStructural(null);
      setForm({ code: '', name: '', type: '', description: '', openingBalance: '', openingBalanceDate: today(), isContraAccount: false, isActive: true, accountCategory: '' });
      setCodeSuggested(false);
    }
    setOpenDlg(true);
  };
  const handleSubmit = async () => {
    const payload = { ...form, openingBalance: Number(form.openingBalance) || 0 };
    try {
      if (editing) {
        await axios.put(`/accounting/chart-of-accounts/${editing}`, payload);
      } else {
        await axios.post('/accounting/chart-of-accounts', payload);
      }
      setOpenDlg(false); fetchAccounts();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`/accounting/chart-of-accounts/${id}`);
      fetchAccounts();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" mb={2}>Chart of Accounts</Typography>
      <Button variant="contained" startIcon={<Add />} onClick={()=>handleOpen()} sx={{ mb:2 }}>Add Account</Button>
      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Code or account name"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
            { key: 'type', label: 'Type', value: filterValues.type || '',
              onChange: (v) => setFilter('type', v),
              options: [
                { value: 'asset', label: 'Asset' },
                { value: 'liability', label: 'Liability' },
                { value: 'equity', label: 'Equity' },
                { value: 'income', label: 'Income' },
                { value: 'expense', label: 'Expense' }
              ] },
            { key: 'category', label: 'Group', value: filterValues.category || '',
              onChange: (v) => setFilter('category', v),
              options: Object.entries(ACCOUNT_CATEGORIES).map(([value, label]) => ({ value, label })) }
        ]}
      />
      <ScrollTable size="small">
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Group</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Opening</TableCell>
              <TableCell align="right">Current</TableCell>
              <TableCell>Contra</TableCell>
              <TableCell>Active</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleAccounts.map(acc=>(
              <TableRow key={acc._id}>
                <TableCell className="pin-left">{acc.code}</TableCell>
                <TableCell>
                  {acc.name}
                  {/* Accounts appear here without anyone adding them: a standard account is
                      created the first time a posting needs it, and a landlord gets a
                      liability account the first time they are charged. Saying so answers
                      "where did this come from?" on the screen rather than in a support
                      call. */}
                  {acc.isSystemCreated && (
                    <Chip
                      label="auto"
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      title="Created automatically the first time something posted to it"
                    />
                  )}
                  {/* A different statement from "auto", and the two overlap only by
                      accident. "auto" says nobody created this row. "built-in" says the
                      posting code reaches this account by its literal code, so re-coding or
                      retiring it would make the code stop finding it — and getAccountByCode
                      would then create a replacement rather than fail. */}
                  {acc.isStructural && (
                    <Chip
                      label="built-in"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      title={acc.structuralUse
                        ? `The system posts here by code — it carries ${acc.structuralUse}. Code, type and active state are fixed.`
                        : 'The system posts here by code. Code, type and active state are fixed.'}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={acc.type} color={{asset:'info',liability:'warning',equity:'secondary',income:'success',expense:'error'}[acc.type]} />
                </TableCell>
                <TableCell>
                  {acc.accountCategory
                    ? <Chip size="small" variant="outlined" label={ACCOUNT_CATEGORIES[acc.accountCategory] || acc.accountCategory} />
                    : '—'}
                </TableCell>
                <TableCell>{acc.description}</TableCell>
                <TableCell align="right">
                  <Balance amount={acc.initialBalance} account={acc} />
                  {/* When the opening balance was dated, which decides which period it
                      opens. Without it an opening balance and a correction posted months
                      later are the same figure on screen. */}
                  {acc.openingBalanceDate && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {asDisplayDate(acc.openingBalanceDate)}
                      {acc.openingBalanceLastAdjusted && (
                        <Box component="span" title={`Last restated ${asDisplayDate(acc.openingBalanceLastAdjusted)}`}>
                          {` · restated ${asDisplayDate(acc.openingBalanceLastAdjusted)}`}
                        </Box>
                      )}
                    </Typography>
                  )}
                </TableCell>
                {/* The balance is the thing people question, so it is the thing that opens
                    the ledger explaining it. A button rather than a clickable row: the row
                    already has Edit and Delete on it, and a row-wide click would fire
                    whenever somebody meant one of those and missed. */}
                <TableCell align="right">
                  {onDrillDown ? (
                    <Button
                      size="small"
                      onClick={() => onDrillDown(acc._id)}
                      sx={{ textTransform: 'none', minWidth: 0, p: 0.25 }}
                      title="See what makes up this balance"
                    >
                      <Balance amount={acc.currentBalance} account={acc} />
                    </Button>
                  ) : (
                    <Balance amount={acc.currentBalance} account={acc} />
                  )}
                </TableCell>
                <TableCell>{acc.isContraAccount? 'Yes':'No'}</TableCell>
                <TableCell><Switch checked={!!acc.isActive} disabled /></TableCell>
                <TableCell className="pin-right">
                  <IconButton size="small" onClick={()=>handleOpen(acc)}><Edit fontSize="small" /></IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!!acc.isStructural}
                    title={acc.isStructural ? 'Built-in accounts cannot be deleted — the posting code would recreate it' : 'Delete'}
                    onClick={()=>handleDelete(acc._id)}
                  ><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {visibleAccounts.length === 0 && <TableRow><TableCell colSpan={10} align="center">No accounts</TableCell></TableRow>}
          </TableBody>
        </ScrollTable>

      <Dialog open={openDlg} onClose={()=>setOpenDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Account' : 'Add Account'}</DialogTitle>
        <DialogContent>
          {/* Said up front rather than as a save error. The name stays editable because
              nothing compares against it — an agency that calls 5010 'Office Lease' should
              be able to say so. */}
          {structural && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This is a built-in account: the system posts to it by code
              {typeof structural === 'string' ? `, and it carries ${structural}` : ''}.
              Its code, type and active state are fixed — changing them would leave the
              posting code unable to find it, and it would create a replacement rather than
              fail. The name and description are yours to change.
            </Alert>
          )}
          <TextField
            margin="normal"
            label="Code"
            fullWidth
            value={form.code}
            onChange={e=>{ setForm(f=>({...f,code:e.target.value})); setCodeSuggested(false); }}
            disabled={!!editing}
            helperText={
              editing ? undefined
                : codeLoading ? 'Finding the next free code…'
                  : codeSuggested ? 'Suggested — change it if your chart is numbered differently'
                    : 'Choose a type and the next free code is filled in for you'
            }
          />
          <TextField margin="normal" label="Name" fullWidth value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} />
          <TextField
            margin="normal"
            select
            label="Type"
            fullWidth
            value={form.type}
            disabled={!!structural}
            helperText={structural
              ? 'Fixed on a built-in account'
              : (form.type === 'asset'
                ? 'Cash, bank, mobile money and suspense accounts are all Assets -- set which under Group below.'
                : 'A suspense account is an Asset: choose Asset, then set its Group to Suspense.')}
            onChange={e=>{
              const type = e.target.value;
              setForm(f=>({...f, type, ...(type !== 'asset' ? { accountCategory: '' } : {})}));
              suggestCode(type);
            }}
          >
            {ACCT_TYPES.map(t=>(<MenuItem key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</MenuItem>))}
          </TextField>
          {/* Only asset accounts hold money, so only they carry a category. This is what a
              payment mode filters on — choose Bank on a payment and you are offered the
              accounts marked Bank here. An unclassified account is offered by no mode. */}
          {form.type === 'asset' && (
            <TextField
              margin="normal"
              select
              label="Group"
              fullWidth
              value={form.accountCategory || ''}
              onChange={e=>setForm(f=>({...f,accountCategory:e.target.value}))}
              helperText={form.accountCategory === 'suspense'
                ? 'Holds money temporarily until it is moved to the right account with a transfer. Never offered to a payment mode.'
                : 'Which payment modes can receive into this account. Suspense parks money to move on later. Leave as Not a money account for receivables, prepayments and the like.'}
            >
              <MenuItem value="">Not a money account</MenuItem>
              {Object.entries(ACCOUNT_CATEGORIES).filter(([key]) => key !== 'other').map(([key,label])=>(
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          )}
          <TextField margin="normal" label="Description" fullWidth value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} />
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              margin="normal"
              label="Opening Balance"
              fullWidth
              type="number"
              value={form.openingBalance}
              onChange={e=>setForm(f=>({...f,openingBalance:e.target.value}))}
              helperText={openingBalanceHint(form)}
            />
            {/* An opening balance is a starting POSITION, so its date decides which period
                it opens. Dated today when the books start in July, the whole opening
                position lands in the wrong month and every report drawn between the two is
                wrong by it. */}
            <TextField
              margin="normal"
              label="As at"
              type="date"
              value={form.openingBalanceDate}
              onChange={e=>setForm(f=>({...f,openingBalanceDate:e.target.value}))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 190 }}
              helperText="The date this balance opens on"
            />
          </Box>
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <Typography>Contra Account?</Typography>
            {/* Looks like a presentation choice and is not. Depreciation refuses to run
                unless 1900 carries this and 1800 does not, and an opening balance reads it
                to decide which side it posts on. */}
            <Switch
              checked={form.isContraAccount}
              disabled={!!structural}
              onChange={e=>setForm(f=>({...f,isContraAccount:e.target.checked}))}
            />
            <Typography>Active?</Typography>
            {/* Retiring a superseded built-in — 4000 Rent Income, say — looks like tidying
                up and does nothing at all until somebody cancels an old invoice. A reversal
                posts back into whatever accounts the original used, and postJournal refuses
                an inactive one, so the failure arrives years later and one screen away. */}
            <Switch
              checked={form.isActive}
              disabled={!!structural}
              onChange={e=>setForm(f=>({...f,isActive:e.target.checked}))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChartOfAccounts;
